import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [authState, setAuthState] = useState('INITIAL_SESSION');
  const [authSource, setAuthSource] = useState(null); // 'supabase' ou 'local'

  // Configuration axios — synchronisé avec le token
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Initialisation unique — sans race condition entre plusieurs useEffect
  useEffect(() => {
    // Timer partagé : annulé par handleSsoMessage dès que le token arrive
    let ssoPendingTimer = null;
    // Guard anti-double setSession (évite deadlock Navigator LockManager)
    let ssoCompleted = false;

    // Listener postMessage SSO — installé avant initAuth pour ne manquer aucun envoi
    const handleSsoMessage = async (event) => {
      // Vérification d'origine stricte (jamais .includes())
      if (event.origin !== 'https://app.innovactive360.cloud') return;
      if (!event.data || event.data.type !== 'SSO_TOKEN' || !event.data.token) return;
      // Bloquer les retries si le SSO est déjà établi
      if (ssoCompleted) return;

      // Annuler le timer d'attente SSO
      if (ssoPendingTimer !== null) { clearTimeout(ssoPendingTimer); ssoPendingTimer = null; }

      const { token: ssoToken, refresh_token: refreshToken } = event.data;

      try {
        const { data: { session }, error } = await supabase.auth.setSession({
          access_token: ssoToken,
          refresh_token: refreshToken || '',
        });

        if (error || !session) {
          // setSession échoué — afficher la page de login plutôt que rester bloqué
          setLoading(false);
          return;
        }

        const _meta = session.user.user_metadata || {};
        const firstName = (typeof _meta.first_name === 'string' ? _meta.first_name : null)
          || (typeof _meta.firstName === 'string' ? _meta.firstName : null)
          || (typeof _meta.full_name === 'string' ? _meta.full_name.split(' ')[0] : null)
          || session.user.email.split('@')[0];
        const lastName = (typeof _meta.last_name === 'string' ? _meta.last_name : null)
          || (typeof _meta.lastName === 'string' ? _meta.lastName : null)
          || (typeof _meta.full_name === 'string' ? _meta.full_name.split(' ').slice(1).join(' ') : null)
          || '';

        const rawRole = session.user.app_metadata?.role || 'user';
        const supabaseUser = {
          id: session.user.id,
          email: session.user.email,
          firstName,
          lastName,
          role: rawRole === 'super_admin' ? 'super-admin' : rawRole,
          provider: session.user.app_metadata?.provider || 'email',
          avatar: (_meta.avatar_url || _meta.picture || null),
        };

        ssoCompleted = true;
        axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
        setToken(session.access_token);
        setUser(supabaseUser);
        setAuthState('SIGNED_IN');
        setAuthSource('supabase');
        setLoading(false);
      } catch (e) {
        // Erreur inattendue — débloquer le chargement
        setLoading(false);
      }
    };

    window.addEventListener('message', handleSsoMessage);

    const initAuth = async () => {
      // PRIORITÉ 0 : SSO en attente — ouverture depuis la plateforme (flag ?sso=1 sans token)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('sso') === '1') {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
        // Handshake SSO_READY : signale à la plateforme que le module est prêt à recevoir le token.
        // La plateforme répond immédiatement au lieu d'attendre un délai fixe.
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.postMessage({ type: 'SSO_READY' }, 'https://app.innovactive360.cloud');
          } catch { /* opener fermé ou cross-origin inattendu */ }
        }
        // Timeout étendu à 12s (couvre les chargements lents sur premier accès sans cache)
        ssoPendingTimer = setTimeout(() => setLoading(false), 12000);
        return; // Ne pas déclencher le finally — setLoading géré par le timer ou handleSsoMessage
      }

      try {
        // ÉTAPE 1 : Session Supabase (SSO depuis la plateforme unifiée)
        try {
          const { data: { session }, error } = await supabase.auth.getSession();

          if (session && !error) {
            const _meta = session.user.user_metadata || {};
            const firstName = (typeof _meta.first_name === 'string' ? _meta.first_name : null)
              || (typeof _meta.firstName === 'string' ? _meta.firstName : null)
              || (typeof _meta.full_name === 'string' ? _meta.full_name.split(' ')[0] : null)
              || session.user.email.split('@')[0];
            const lastName = (typeof _meta.last_name === 'string' ? _meta.last_name : null)
              || (typeof _meta.lastName === 'string' ? _meta.lastName : null)
              || (typeof _meta.full_name === 'string' ? _meta.full_name.split(' ').slice(1).join(' ') : null)
              || '';

            const rawRole = session.user.app_metadata?.role || 'user';
            const supabaseUser = {
              id: session.user.id,
              email: session.user.email,
              firstName,
              lastName,
              role: rawRole === 'super_admin' ? 'super-admin' : rawRole,
              provider: session.user.app_metadata?.provider || 'email',
              avatar: (typeof _meta.avatar_url === 'string' ? _meta.avatar_url : null)
                || (typeof _meta.picture === 'string' ? _meta.picture : null)
                || null
            };

            axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
            setUser(supabaseUser);
            setToken(session.access_token);
            setAuthSource('supabase');
            setAuthState('SIGNED_IN');
            setLoading(false);
            return;
          }
        } catch (supabaseError) {
          // Session Supabase non disponible — continuer
        }

      } catch (error) {
        // Erreur initialisation auth
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Écouter les changements de session Supabase
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // SSO postMessage déjà traité — évite double mise à jour
        if (ssoCompleted) return;
        const _meta = session.user.user_metadata || {};
        const firstName = (typeof _meta.first_name === 'string' ? _meta.first_name : null)
          || (typeof _meta.firstName === 'string' ? _meta.firstName : null)
          || (typeof _meta.full_name === 'string' ? _meta.full_name.split(' ')[0] : null)
          || session.user.email.split('@')[0];
        const lastName = (typeof _meta.last_name === 'string' ? _meta.last_name : null)
          || (typeof _meta.lastName === 'string' ? _meta.lastName : null)
          || (typeof _meta.full_name === 'string' ? _meta.full_name.split(' ').slice(1).join(' ') : null)
          || '';

        const rawRole = session.user.app_metadata?.role || 'user';
        const supabaseUser = {
          id: session.user.id,
          email: session.user.email,
          firstName,
          lastName,
          role: rawRole === 'super_admin' ? 'super-admin' : rawRole,
          provider: session.user.app_metadata?.provider || 'email',
          avatar: (typeof _meta.avatar_url === 'string' ? _meta.avatar_url : null)
            || (typeof _meta.picture === 'string' ? _meta.picture : null)
            || null
        };

        axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
        setUser(supabaseUser);
        setToken(session.access_token);
        setAuthSource('supabase');
        setAuthState('SIGNED_IN');
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setToken(null);
        setAuthSource(null);
        setAuthState('SIGNED_OUT');
        delete axios.defaults.headers.common['Authorization'];
      }
    });

    return () => {
      clearTimeout(ssoPendingTimer);
      subscription?.unsubscribe();
      window.removeEventListener('message', handleSsoMessage);
    };
  }, []);

  // Login local (fallback si pas de SSO)
  const login = async (email, password, captchaToken) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password,
        'cf-turnstile-response': captchaToken || undefined,
      });
      const { token: newToken, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      setAuthState('SIGNED_IN');
      setAuthSource('local');

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur de connexion'
      };
    }
  };

  // Register local
  const register = async (userData) => {
    try {
      const { captchaToken, ...fields } = userData;
      const response = await axios.post('/api/auth/register', {
        ...fields,
        'cf-turnstile-response': captchaToken || undefined,
      });
      const { token: newToken, user: newUser } = response.data;

      // Confirmation par e-mail requise : pas encore de session
      if (!newToken) return { success: true, needsConfirmation: true };

      setToken(newToken);
      setUser(newUser);
      setAuthState('SIGNED_IN');
      setAuthSource('local');

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur d\'inscription'
      };
    }
  };

  // Logout (gère Supabase ET local)
  const logout = async () => {
    if (authSource === 'supabase') {
      await supabase.auth.signOut();
    }
    setToken(null);
    setUser(null);
    setAuthSource(null);
    setAuthState('SIGNED_OUT');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    loading,
    authState,
    authSource,
    login,
    register,
    logout,
    isAuthenticated: !!user && authState === 'SIGNED_IN'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
